import {Button, Col, DatePicker, Divider, Form, Input, Row, Select, Space, notification, InputNumber} from "antd";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import PermissionCheck from "../../Common/PermissionCheck";
import { useUpdateSeason } from "~entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { Genders } from "~enums/genders";
import { LeaderCriteriaSelect } from "./PermissionControls/LeaderCriteriaSelect";
import { MatchProfileSelect } from "./PermissionControls/MatchProfileSelect";
import { StandingsConfigurationSelect } from "./PermissionControls/StandingsConfigurationSelect";
import { Representations } from "~enums/representations";
import CustomSelect from "~components/CustomSelect";
import { RosterStatuses } from "~enums/rosterStatuses";
import { useNotification } from "~hooks/useNotification";

const PermissionName = PermissionNames.SEASON;
const STATUS = ["ACTIVE", "COMPLETE", "DRAFT", "INACTIVE", "PENDING"];
const GENDER = [Genders.Male, Genders.Female, Genders.Mixed, Genders.Unknown]
const REPRESENTATION = [Representations.Club, Representations.Country, Representations.Person, Representations.Region, Representations.State];

const YEAR = () => {
  const yearList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    yearList.push(i);
  }
  return yearList;
};

const showDiff = (oldObj, newObj) => {
  const updateObj = {};
  Object.keys(newObj)?.forEach((key) => {
    if (newObj[key] !== oldObj[key]) {
      updateObj[key] = newObj[key];
    }
  });

  return updateObj;
};

//TODO: roster.related check correct permissions

const EditSeasonForm = ({ seasonData }) => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const {
    seasonStandards: STANDARDS,
    ageGroups: AGE_GROUP,
    disciplines: DISCIPLINE,
    seasonTypes,
    uiConfiguration
  } = useSportConfiguration();
  const { seasonUpsert: seasonUpsertUiConfiguration } = uiConfiguration.pages;

  const { mutate: updateFn, isPending: isUpdatePending } = useUpdateSeason();
  const season = seasonData?.data?.[0];
  const includesData = seasonData?.includes?.resources;

  const handleRequest = async (data) => {
    updateFn(data);
  };
  const notification = useNotification();

  const handleSend = async () => {
    const updateData = showDiff(season, await formatFormData());
    if (Object.keys(updateData)?.length > 0) {
      handleRequest(updateData);
    } else {
      notification.warning({
        message: "Nothing to update",
        placement: "bottomRight",
      });
    }
  };

  const formatFormData = async () => {
    const formData = await form.validateFields();
    const formattedValues = {};
    Object.keys(formData)?.forEach((key) => {
      if (key === "startDate" || key === "endDate") {
        formattedValues[key] = formData[key].format("YYYY-MM-DD");
      } else if (formData[key] === "") {
        formattedValues[key] = null;
      } else {
        formattedValues[key] = formData[key];
      }
    });

    return formattedValues;
  };

  return (
    <>
      <Form
        form={form}
        layout={"inline"}
        labelCol={{ span: 11 }}
        wrapperCol={{ span: 13 }}
        colon={false}
        initialValues={{
          discipline: season?.discipline,
          nameLocal: season?.nameLocal,
          nameLatin: season?.nameLatin,
          nameShortLocal: season?.nameShortLocal,
          nameShortLatin: season?.nameShortLatin,
          year: season?.year,
          seasonType: season?.seasonType,
          status: season?.status,
          startDate: dayjs(season?.startDate, "YYYY-MM-DD"),
          endDate: dayjs(season?.endDate, "YYYY-MM-DD"),
          gender: season?.gender,
          ageGroup: season?.ageGroup,
          standard: season?.standard,
          representation: season?.representation,
          standingConfigurationId: season?.standingConfigurationId,
          profileId: season?.profileId,
          leaderCriteriaId: season?.leaderCriteriaId,
          externalId: season?.externalId,
          lockStandings: season?.lockStandings,
          includeInStatistics: season?.includeInStatistics,
          videoProduction: season?.videoProduction,
          liveDataAvailable: season?.liveDataAvailable,
          liveVideoAvailable: season?.liveVideoAvailable,
          rosterConfiguration: {
            fixtureRosterLock: season?.rosterConfiguration?.fixtureRosterLock,
            seasonRosterLock: season?.rosterConfiguration?.seasonRosterLock,
            windowCloseDate: season?.rosterConfiguration?.windowCloseDate
              ? dayjs(season?.rosterConfiguration?.windowCloseDate, "YYYY-MM-DD")
              : undefined,
            windowOpenDate: season?.rosterConfiguration?.windowOpenDate
              ? dayjs(season?.rosterConfiguration?.windowOpenDate, "YYYY-MM-DD")
              : undefined,
            fixtureBibEdit: season?.rosterConfiguration?.fixtureBibEdit ?? false,
            seasonRosterStatusAllowed: season?.rosterConfiguration?.seasonRosterStatusAllowed ?? [],
            publishFixtureRosterRolesInMinutes:season?.rosterConfiguration?.publishFixtureRosterRolesInMinutes,
            publishSeasonRosterRolesInHours: season?.rosterConfiguration?.publishSeasonRosterRolesInHours,
          },
        }}
      >
        <Row justify={"start"}>
          <Col span={12}>
            <Form.Item
              name={"nameLocal"}
              label={formatMessage({
                id: "nameLocal",
                defaultMessage: "Local Name",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.local.name.item",
                  defaultMessage: "The name of the {item} in the local Language",
                },
                {
                  item: formatMessage({
                    id: "season",
                    defaultMessage: "Season",
                  }),
                },
              )}
              required
              rules={[{ required: true }]}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"nameLatin"}
              label={formatMessage({
                id: "nameLatin",
                defaultMessage: "Latin Name",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.latin.name.item",
                  defaultMessage: "The name of the {item} in latin characters",
                },
                {
                  item: formatMessage({
                    id: "season",
                    defaultMessage: "Season",
                  }),
                },
              )}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"nameShortLocal"}
              label={formatMessage({
                id: "name.local.abbreviation",
                defaultMessage: "Local Abbr",
              })}
              tooltip={formatMessage({
                id: "form.help.local.abbreviation",
                defaultMessage: "An abbreviation/short name in the local language",
              })}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"nameShortLatin"}
              label={formatMessage({
                id: "name.latin.abbreviation",
                defaultMessage: "Latin Abbr",
              })}
              tooltip={formatMessage({
                id: "form.help.latin.abbreviation",
                defaultMessage: "An abbreviation/short name in latin characters",
              })}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"year"}
              label={formatMessage({
                id: "year",
                defaultMessage: "Year",
              })}
              tooltip={formatMessage({
                id: "form.help.season.year",
                defaultMessage: "Year of the season",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select showSearch>
                {YEAR().map((year) => (
                  <Select.Option value={year} key={year}>
                    {year}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"seasonType"}
              label={formatMessage({
                id: "season.type",
                defaultMessage: "Season Type",
              })}
              tooltip={formatMessage({
                id: "form.help.season.type",
                defaultMessage: "The type of matches added to this season",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select options={[...seasonTypes.map(type => ({ value: type, label: type }))]}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"status"}
              label={formatMessage({
                id: "status",
                defaultMessage: "Status",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.status.item",
                  defaultMessage: "What is the current status of this {item}?",
                },
                {
                  item: formatMessage({
                    id: "season",
                    defaultMessage: "Season",
                  }),
                },
              )}
              required
              rules={[{ required: true }]}
            >
              <Select>
                {STATUS?.map((status) => (
                  <Select.Option value={status} key={status}>
                    {status}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"startDate"}
              label={formatMessage({
                id: "date.start",
                defaultMessage: "Start Date",
              })}
              tooltip={formatMessage({
                id: "form.help.season.start.date",
                defaultMessage: "Season start date",
              })}
              required
              rules={[{ required: true }]}
            >
              <DatePicker allowClear={false} size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"endDate"}
              label={formatMessage({
                id: "date.end",
                defaultMessage: "End Date",
              })}
              tooltip={formatMessage({
                id: "form.help.season.end.date",
                defaultMessage: "Season end date",
              })}
              required
              rules={[{ required: true }]}
            >
              <DatePicker allowClear={false} size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"gender"}
              label={formatMessage({
                id: "gender",
                defaultMessage: "Gender",
              })}
              tooltip={formatMessage({
                id: "form.help.season.gender",
                defaultMessage: "The gender of the participants in the season",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select>
                {GENDER?.map((gender) => (
                  <Select.Option value={gender} key={gender}>
                    {gender}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"ageGroup"}
              label={formatMessage({
                id: "ageGroup",
                defaultMessage: "Age Group",
              })}
              tooltip={formatMessage({
                id: "form.help.season.age.group",
                defaultMessage: "The age group of the season",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select showSearch>
                {AGE_GROUP?.map((ageGroup) => (
                  <Select.Option value={ageGroup} key={ageGroup}>
                    {ageGroup}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"standard"}
              label={formatMessage({
                id: "standard",
                defaultMessage: "Standard",
              })}
              tooltip={formatMessage({
                id: "form.help.season.standard",
                defaultMessage: "The playing standard of the season",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select showSearch>
                {STANDARDS?.map((standard) => (
                  <Select.Option value={standard} key={standard}>
                    {standard}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"representation"}
              label={formatMessage({
                id: "representation",
                defaultMessage: "Representation",
              })}
              tooltip={formatMessage({
                id: "form.help.season.representation",
                defaultMessage: "What level are the competitors representing",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select showSearch>
                {REPRESENTATION?.map((representation) => (
                  <Select.Option value={representation} key={representation}>
                    {representation}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"standingConfigurationId"}
              label={formatMessage({
                id: "standings.configuration",
                defaultMessage: "Standings Configuration",
              })}
              tooltip={formatMessage({
                id: "form.help.season.standings.configuration",
                defaultMessage: "What standings configuration belongs to this season?",
              })}
            >
              <PermissionCheck
                permissionKeys={[PermissionNames.STANDING_CONFIGURATION]}
                componentToRender={
                  <Input
                    value={includesData?.standingConfigurations?.[season?.standingConfigurationId]?.nameLocal}
                    disabled
                  />
                }
              >
                <StandingsConfigurationSelect
                  name={"standingConfigurationId"}
                  defaultValue={season?.standingConfigurationId}
                />
              </PermissionCheck>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"profileId"}
              label={formatMessage({
                id: "match.profile",
                defaultMessage: "Match Profile",
              })}
              tooltip={formatMessage({
                id: "form.help.season.match.profile",
                defaultMessage: "What match profile belongs to this season?",
              })}
            >
              <PermissionCheck
                permissionKeys={[PermissionNames.MATCH_PROFILE]}
                componentToRender={<Input value={includesData?.fixtureProfiles?.[season?.profileId]?.name} disabled/>}
              >
                <MatchProfileSelect name={"profileId"} defaultValue={season?.profileId}/>
              </PermissionCheck>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"leaderCriteriaId"}
              label={formatMessage({
                id: "leaders.qualification",
                defaultMessage: "Leaders Qualification",
              })}
              tooltip={formatMessage({
                id: "form.help.season.leaders.qualification",
                defaultMessage: "What leader qualification belongs to this season?",
              })}
            >
              <PermissionCheck
                permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}
                componentToRender={
                  <Input value={includesData?.leaderCriteria?.[season?.leaderCriteriaId]?.name} disabled/>
                }
              >
                <LeaderCriteriaSelect name={"leaderCriteriaId"} defaultValue={season?.leaderCriteriaId}/>
              </PermissionCheck>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"externalId"}
              label={formatMessage({
                id: "externalId",
                defaultMessage: "External Id",
              })}
              tooltip={formatMessage({
                id: "externalId",
                defaultMessage: "External Id",
              })}
            >
              <Input/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"lockStandings"}
              label={formatMessage({
                id: "standings.lock",
                defaultMessage: "Lock Standings",
              })}
              tooltip={formatMessage({
                id: "form.help.season.lock.standings",
                defaultMessage: "Is the standings generation locked?",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"includeInStatistics"}
              label={formatMessage({
                id: "includeInStatistics",
                defaultMessage: "Include in statistics",
              })}
              tooltip={formatMessage({
                id: "form.help.season.include.in.statistics",
                defaultMessage: "Include this season in calculated statistics?",
              })}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={"videoProduction"}
              label={formatMessage({
                id: "video.production",
                defaultMessage: "Video Production",
              })}
              tooltip={formatMessage({
                id: "form.help.season.video.production",
                defaultMessage: "Data synchronization strategy with video production systems",
              })}
            >
              <Select>
                <Select.Option value={"NONE"}>None</Select.Option>
                <Select.Option value={"AUTOMATED"}>Automated</Select.Option>
                <Select.Option value={"MANUAL"}>Manual</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={"liveDataAvailable"}
              label={formatMessage({
                id: "liveDataAvailable",
                defaultMessage: "Live Data available",
              })}
              tooltip={formatMessage({
                id: "form.help.season.live.data.available",
                defaultMessage: "Is live data available for this season?",
              })}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={"liveVideoAvailable"}
              label={formatMessage({
                id: "liveVideoAvailable",
                defaultMessage: "Live Video available",
              })}
              tooltip={formatMessage({
                id: "form.help.season.live.video.available",
                defaultMessage: "Is live video available for this season?",
              })}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          {seasonUpsertUiConfiguration.disciplines.enabled && (
            <Col span={12}>
              <Form.Item
                name={"discipline"}
                label={formatMessage({
                  id: "discipline",
                  defaultMessage: "Discipline",
                })}
                tooltip={formatMessage({
                  id: "form.help.season.discipline",
                  defaultMessage: "What type of discipline will this season be?",
                })}
                required
                rules={[{ required: true }]}
              >
                <Select showSearch>
                  {seasonUpsertUiConfiguration.disciplines.isNullable && (
                    <Select.Option value={"NONE"}>--</Select.Option>
                  )}
                  {DISCIPLINE?.map((discipline) => (
                    <Select.Option value={discipline} key={discipline}>
                      {discipline}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Divider plain>
            {formatMessage({
              id: "roster.related",
              defaultMessage: "Roster Related",
            })}
          </Divider>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "windowOpenDate"]}
              label={formatMessage({
                id: "season.window.open.date",
                defaultMessage: "Window Open Date",
              })}
              tooltip={formatMessage({
                id: "form.help.season.window.open.date",
                defaultMessage: "What date can a Season Roster be submitted from for this Season?",
              })}
            >
              <DatePicker allowClear={true} size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "windowCloseDate"]}
              label={formatMessage({
                id: "season.window.close.date",
                defaultMessage: "Window Close Date",
              })}
              tooltip={formatMessage({
                id: "form.help.season.window.close.date",
                defaultMessage: "What is the last date a Season Roster can be submitted from for this Season?",
              })}
            >
              <DatePicker allowClear={true} size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "seasonRosterLock"]}
              label={formatMessage({
                id: "season.roster.lock",
                defaultMessage: "Lock Season Roster?",
              })}
              tooltip={formatMessage({
                id: "form.help.season.roster.lock",
                defaultMessage:
                  "Make Season Rosters un-editable when Season Roster Registration Window Close Date has elapsed.",
              })}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "fixtureRosterLock"]}
              label={formatMessage({
                id: "season.match.roster.lock",
                defaultMessage: "Lock Match Rosters?",
              })}
              tooltip={formatMessage({
                id: "form.help.season.match.roster.lock",
                defaultMessage: "Make Match Rosters un-editable when Match Status = Scheduled**",
              })}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "fixtureBibEdit"]}
              label={formatMessage({
                id: "season.roster.player.number.edit.allowed",
              })}
              tooltip={formatMessage({
                id: "form.help.roster.player.number.edit.allowed",
              })}
            >
              <Select>
                <Select.Option value>{formatMessage({ id: "yes" })}</Select.Option>
                <Select.Option value={false}>{formatMessage({ id: "no" })}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "seasonRosterStatusAllowed"]}
              label={formatMessage({
                id: "season.roster.player.status.allowed",
              })}
              tooltip={formatMessage({
                id: "form.help.roster.player.status.allowed",
              })}
            >
              <CustomSelect mode="multiple" allowClear>
                {Object.values(RosterStatuses).map(status => (
                  <Select.Option value={status} key={status}>
                    {formatMessage({ id: `roster.status.${status}` })}
                  </Select.Option>
                ))}
              </CustomSelect>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "publishSeasonRosterRolesInHours"]}
              label={formatMessage({
                id: "season.roster.publish.ahead.hours",
              })}
              tooltip={formatMessage({
                id: "form.help.roster.publish.seasonRoster.ahead.hours",
              })}
            >
              <InputNumber style={{width: "100%"}}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={["rosterConfiguration", "publishFixtureRosterRolesInMinutes"]}
              label={formatMessage({
                id: "season.roster.publish.fixtureRoster.ahead.minutes",
              })}
              tooltip={formatMessage({
                id: "form.help.roster.publish.fixtureRoster.ahead.minutes",
              })}
            >
              <InputNumber style={{width: "100%"}}/>
            </Form.Item>
          </Col>
          <PermissionCheck permissionKeys={[PermissionNames.SEASON]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Divider plain>
              {formatMessage({
                id: "date.time.stamps",
                defaultMessage: "Date/Time Stamps",
              })}
            </Divider>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    {formatMessage({
                      id: "created_at",
                      defaultMessage: "Created At",
                    })}
                    <i className="fas fa-xs fa-lock"/>
                  </Space>
                }
              >
                <Input style={{ color: "black" }} disabled value={dayjs(season?.added).format("DD/MM/YYYY")}/>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    {formatMessage({
                      id: "updated_at",
                      defaultMessage: "Updated At",
                    })}
                    <i className="fas fa-xs fa-lock"/>
                  </Space>
                }
              >
                <Input style={{ color: "black" }} disabled value={dayjs(season?.updated).format("DD/MM/YYYY")}/>
              </Form.Item>
            </Col>
          </PermissionCheck>
        </Row>
      </Form>
      <Row justify={"center"} className={"mt-3"}>
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
          <Button loading={isUpdatePending} type={"primary"} size={"large"} onClick={handleSend}>
            {formatMessage({ id: "update", defaultMessage: "Update" })}
          </Button>
        </PermissionCheck>
      </Row>
    </>
  );
};

export default EditSeasonForm;
